<script setup>

import {onMounted, ref} from "vue";
import FrostedBar from "../../../layout/pageHeadings/frostedBar.vue";
import { useNotificationStore } from '@/stores/NotificationStore.js'

const notificationStore = useNotificationStore()
const props = defineProps({
    automationId: {
        type: Number,
        required: true,
    }
})

const newsletter = ref(null)
const editingName = ref(false)
const copying = ref(false)
const showSendPreviewEmailModal = ref(false)
const previewEmailAddress = ref('')

const loadAutomationData = async () => {
    if (props.automationId === null) {
        return
    }
    const res = await axios.get(route('api.bo.crm.automations.summary', {automation_id: props.automationId}))

    newsletter.value = res.data.automation;
}

const sendPreviewEmail = async (email) => {
    if (email === 'myself') {
        email = window.myflow.user.email
    }

    const res = await axios.post(route('crm.communications.automations.dispatchTestMessages', newsletter.value.id), {
        email: email
    })

    if (res.data.success) {
        notificationStore.addNotification({
            type: 'success',
            message: _mft('communication:composer.previewEmailSent'),
            dismissAfter: 2500,
        })
    }
}

const updateNewsletterName = async () => {
    if (newsletter.value.name.length === 0) {
        return;
    }

    const res = await axios.put(route('api.bo.crm.newsletters.updateName', newsletter.value.id), {
        name: newsletter.value.name
    })

    if (res.data.success) {
        editingName.value = false
    }
}

const copyAutomation = async () => {
    if (copying.value === true) {
        return;
    }
    copying.value = true
    const res = await axios.post(route('crm.communications.automations.clone', newsletter.value.id))

    if (res.data.success) {
        window.location.href = route('bo.crm.newsletters.edit', res.data.automation_id)
    }
}

const cancelSchedule = async () => {
    const res = await axios.post(route('api.bo.crm.automations.cancel', {automation_id: props.automationId}))
    window.location.reload();
}

onMounted(() => {
    loadAutomationData()
})

</script>
<template>
    <div v-if="newsletter">
        <frosted-bar>
            <div class="flex items-center justify-between space-x-4 f">
                <div class="flex space-x-1 items-start">
                    <h1>
                        {{ newsletter.name }}
                    </h1>
                    <button-transparent
                        size="xs"
                        @click="editingName = true"
                    >
                        <i class="fa-regular fa-pen text-xs text-gray-400"></i>
                    </button-transparent>
                </div>

                <div class="flex items-center space-x-2">
                    <button-more-options
                        :label="_mft('communication:composer.preview')"
                        alignment="right"
                        :cancel-on-outside-click="false"
                    >
                        <button-more-options-option
                            @click=""
                        >
                            <a
                                :href="route('crm.communications.automations.preview', newsletter.id)"
                                target="_blank"
                                class="whitespace-nowrap"
                            >
                                {{ _mft('communication:previewInBrowser') }}
                            </a>
                        </button-more-options-option>
                        <button-more-options-option>
                            <span
                                @click="sendPreviewEmail('myself')"
                                class="whitespace-nowrap"
                            >
                                {{ _mft('communication:sendCopyToSelf') }}
                            </span>
                        </button-more-options-option>
                        <button-more-options-option
                            @click="showSendPreviewEmailModal = true"
                        >
                            <span class="whitespace-nowrap">
                                {{ _mft('communication:sendCopyToSomeoneElse') }}
                            </span>
                        </button-more-options-option>
                    </button-more-options>

                    <button-secondary
                        @click="copyAutomation"
                        :disabled="copying"
                    >{{ _mft('communication:copyToNewCommunication') }}</button-secondary>

                    <button-danger
                        v-if="newsletter.scheduled"
                        @click="cancelSchedule"
                    >{{ _mft('shared:action.cancel') }}</button-danger>
                </div>
            </div>

            <template #navigation>
                <a
                    :href="route('bo.crm.newsletters.list')"
                    class="mf-link text-xs"
                >
                    &larr; {{ _mft('communication:newsletter.domainNamePlural') }}
                </a>
            </template>
        </frosted-bar>


        <div class="grid grid-cols-2 gap-4 my-4">
            <card-basic
                v-if="!newsletter.scheduled && !newsletter.sent"
            >
                <div>
                    <h2 class="text-md flex justify-between space-x-2">
                        <span>
                            <i class="fa-regular fa-file mr-1 text-gray-400"></i>
                            {{ _mft('communication:automation.thisIsADraft') }}
                        </span>

                        <span>{{ newsletter.scheduled_at.user_tz_display }}</span>
                    </h2>
                    <div class="flex justify-end">
                        <button-danger
                            v-if="newsletter.scheduled"
                            size="sm"
                            @click="cancelSchedule"
                            class="mt-2"
                        >{{ _mft('shared:action.cancel') }}</button-danger>
                    </div>
                </div>
            </card-basic>

            <card-basic
                v-if="newsletter.scheduled"
            >
                <div>
                    <h2 class="text-md flex justify-between space-x-2">
                        <span>
                            <i class="fa-regular fa-clock mr-1 text-gray-400"></i>
                            {{ _mft('communication:automation.scheduledAt') }}
                        </span>

                        <span>{{ newsletter.scheduled_at.user_tz_display }}</span>
                    </h2>
                    <div class="flex justify-end">
                        <button-danger
                            size="sm"
                            @click="cancelSchedule"
                            class="mt-2"
                        >{{ _mft('shared:action.cancel') }}</button-danger>
                    </div>
                </div>
            </card-basic>

            <card-basic
                v-if="newsletter.sent"
            >
                <div>
                    <h2 class="text-md">
                        <i class="fa-regular fa-clock mr-1 text-gray-400"></i>
                        {{ _mft('communication:automation.sentAt') }}
                    </h2>
                    <span>{{ newsletter.sent_at.user_tz_display }}</span>
                </div>
            </card-basic>


            <card-basic>
                <div>
                    <h2 class="text-md flex justify-between space-x-2">
                        <span>
                            <i class="fa-regular fa-tags mr-1 text-gray-400"></i>
                            {{ _mft('communication:recipients') }}
                        </span>
                    </h2>

                    <ul class="mb-4 flex flex-wrap">
                        <li
                            v-for="(tag, i) in newsletter.included_tags"
                            :key="i"
                            class="mr-2 mb-1 text-black px-1.5 py-0.5 border flex items-center border-gray-300 rounded-full"
                            :style="tag.color ? {backgroundColor: tag.color, color: tag.textcolor} : {}"
                        >
                            <component
                                :is="tag.description ? 'container-with-tooltip' : 'span'"
                                class="text-xs cursor-default"
                            >
                                {{ tag.name }}
                                <template
                                    v-if="tag.description"
                                    #tooltip
                                >
                                    <div class="block w-24 text-xxs text-black">
                                        {{ tag.description }}
                                    </div>
                                </template>
                            </component>
                        </li>
                    </ul>


                    <div
                        v-if="newsletter.excluded_tags.length > 0"
                        class="mt-8"
                    >
                        <p class="text-gray-500">
                            {{ _mft('communication:tags.excludingRecipients') }}
                        </p>
                        <ul class="mb-4 flex flex-wrap">
                            <li
                                v-for="(tag, i) in newsletter.excluded_tags"
                                :key="i"
                                class="mr-2 mb-1 text-black px-1.5 py-0.5 border flex items-center border-gray-300 rounded-full"
                                :style="tag.color ? {backgroundColor: tag.color, color: tag.textcolor} : {}"
                            >
                                <component
                                    :is="tag.description ? 'container-with-tooltip' : 'span'"
                                    class="text-xs cursor-default"
                                >
                                    {{ tag.name }}
                                    <template
                                        v-if="tag.description"
                                        #tooltip
                                    >
                                        <div class="block w-24 text-xxs text-black">
                                            {{ tag.description }}
                                        </div>
                                    </template>
                                </component>
                            </li>
                        </ul>
                    </div>

                </div>
            </card-basic>

            <card-basic>
                <div>
                    <h2 class="text-md flex justify-between space-x-2">
                        <span>
                            <i class="fa-regular fa-magnifying-glass-chart mr-1 text-gray-400"></i>
                            {{ _mft('communication:statistics.results.title') }}
                        </span>
                    </h2>

                    <communications-stats
                        v-if="newsletter.sent && newsletter.stats"
                        class="flex space-x-4"
                        :stats="newsletter.stats"
                    ></communications-stats>

                    <div class="mt-2 flex space-x-2 items-start">
                        <a
                            :href="route('crm.leads', {automationId: automationId, automationFilter: 'opened', automationName: newsletter.name})"
                            target="_blank"
                        >
                            <button-secondary size="sm" class="w-full justify-center">
                                <span>
                                    {{ _mft('communication:automation.filter.opened') }}
                                </span>
                                <i class="fa-solid fa-up-right-from-square ml-2 text-gray-400"></i>
                            </button-secondary>
                        </a>

                        <a
                            :href="route('crm.leads', {automationId: automationId, automationFilter: 'not-opened', automationName: newsletter.name})"
                            target="_blank"
                        >
                            <button-secondary size="sm" class="w-full justify-center">
                                <span>
                                    {{ _mft('communication:automation.filter.notOpened') }}
                                </span>
                                <i class="fa-solid fa-up-right-from-square ml-2 text-gray-400"></i>
                            </button-secondary>
                        </a>

                        <a
                            :href="route('crm.leads', {automationId: automationId, automationFilter: 'not-delivered', automationName: newsletter.name})"
                            target="_blank"
                        >
                            <button-secondary size="sm" class="w-full justify-center">
                                <span>
                                    {{ _mft('communication:automation.filter.notDelivered') }}
                                </span>
                                <i class="fa-solid fa-up-right-from-square ml-2 text-gray-400"></i>
                            </button-secondary>
                        </a>
                    </div>

                </div>
            </card-basic>
        </div>

        <modal-basic
            :open="showSendPreviewEmailModal"
        >
            <template #title>
                {{ _mft('communication:sendCopy') }}
            </template>
            <template #body>
                <div>
                    <div class="space-y-4">
                        <input-text
                            label="Email address to send a copy to"
                            v-model:content="previewEmailAddress"
                        ></input-text>
                    </div>
                    <div class="flex items-center justify-end space-x-4 mt-4">
                        <button-base
                            @click="showSendPreviewEmailModal = false"
                        >
                            {{ _mft('shared:cancel') }}
                        </button-base>
                        <button-primary
                            :disabled="! previewEmailAddress || previewEmailAddress.length === 0"
                            @click="sendPreviewEmail(previewEmailAddress); showSendPreviewEmailModal = false"
                        >
                            {{ _mft('communication:sendCopy') }}
                        </button-primary>
                    </div>
                </div>
            </template>
        </modal-basic>


        <modal-basic
            :open="editingName"
        >
            <template #title>
                {{ _mft('communication:changeName') }}
            </template>
            <template #body>
                <div>
                    <div class="space-y-4">
                        <input-text
                            :label="_mft('communication:automationName')"
                            maxlength="250"
                            v-model:content="newsletter.name"
                            autofocus
                        ></input-text>
                    </div>
                    <div class="flex items-center justify-end space-x-4 mt-4">
                        <button-base
                            @click="editingName = false"
                        >
                            {{ _mft('shared:cancel') }}
                        </button-base>
                        <button-primary
                            :disabled="newsletter.name.length === 0"
                            @click="updateNewsletterName"
                        >
                            {{ _mft('shared:action.update') }}
                        </button-primary>
                    </div>
                </div>
            </template>
        </modal-basic>
    </div>
</template>
